import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as fromActions from '@redux/modules/ctm-mgmt/actions';
import { switchMap, tap, withLatestFrom, catchError } from 'rxjs/operators';
import { firstValueFrom, of } from 'rxjs';
import {
  selectAssignedVins,
  selectAvailableVins,
  selectCtmMgmtProfiles,
  selectCtmMgmtSignals,
  selectAssignedSignals,
  selectAvailableSignals,
  selectProfile, selectCtmMgmtFallbacks,
  selectCtmMgmtVins,
} from '@redux/modules/ctm-mgmt';
import { Store } from '@ngrx/store';
import {
  CtmConfigurationVehicleProfileService, CtmFallbackService,
  CtmProfileService,
  CtmSignalService,
} from '@services/api/sdf-ctm-configuration-management-api.service';

@Injectable()
export class CtmMgmtEffects {
  private readonly signalsPageSize = 1000;


  setCtmMgmtProfilesFilters$ = createEffect(() => this.actions$.pipe(
    ofType(fromActions.setCtmMgmtProfilesFilters),
    tap(() => {
      this.store.dispatch(fromActions.setCtmMgmtProfilesPaginator({ paginator: { page: 1, pageSize: 10 } }));
    }),
  ), { dispatch: false });

  getCtmMgmtProfilesList$ = createEffect(() => this.actions$.pipe(
    ofType(fromActions.getCtmMgmtProfilesList,
      fromActions.setCtmMgmtProfilesPaginator),
    withLatestFrom(
      this.store.select(selectCtmMgmtProfiles),
    ),
    tap(() => this.store.dispatch(fromActions.setCtmMgmtProfilesLoading({ isLoading: true }))),
    switchMap(async ([_, profiles]) => {
      const profilesData = await firstValueFrom(this.ctmProfileService
        .getProfiles(
          profiles.paginator.page,
          profiles.paginator.pageSize,
          profiles.filters.qSearch ? profiles.filters.qSearch : undefined));
      return [
        fromActions.setCtmMgmtProfilesList({ response: profilesData }),
        fromActions.setCtmMgmtProfilesLoading({ isLoading: false }),
      ];
    }),
    catchError(() => of([
      fromActions.setCtmMgmtProfilesList({ response: { items: [], count: 0 } }),
      fromActions.setCtmMgmtProfilesLoading({ isLoading: false }),
    ])),
    switchMap((actions) => [...actions]),
  ));

  setCtmMgmtFallbacksFilters$ = createEffect(() => this.actions$.pipe(
    ofType(fromActions.setCtmMgmtFallbacksFilters),
    tap(() => {
      this.store.dispatch(fromActions.setCtmMgmtFallbacksPaginator({ paginator: { page: 1, pageSize: 10 } }));
    }),
  ), { dispatch: false });

  getCtmMgmtFallbacksList$ = createEffect(() => this.actions$.pipe(
    ofType(fromActions.getCtmMgmtFallbacksList,
      fromActions.setCtmMgmtFallbacksPaginator),
    withLatestFrom(
      this.store.select(selectCtmMgmtFallbacks),
    ),
    tap(() => this.store.dispatch(fromActions.setCtmMgmtFallbacksLoading({ isLoading: true }))),
    switchMap(async ([_, fallbacks]) => {
      const fallbacksData = await firstValueFrom(this.ctmFallbackService
        .getFallbacks(
          fallbacks.paginator.page,
          fallbacks.paginator.pageSize,
          fallbacks.filters.qSearch ? fallbacks.filters.qSearch : undefined));
      return [
        fromActions.setCtmMgmtFallbacksList({ response: fallbacksData }),
        fromActions.setCtmMgmtFallbacksLoading({ isLoading: false }),
      ];
    }),
    catchError(() => of([
      fromActions.setCtmMgmtFallbacksList({ response: { items: [], count: 0 } }),
      fromActions.setCtmMgmtFallbacksLoading({ isLoading: false }),
    ])),
    switchMap((actions) => [...actions]),
  ));

  setCtmMgmtVinsFilters$ = createEffect(() => this.actions$.pipe(
    ofType(fromActions.setCtmMgmtVinsFilters),
    tap(() => {
      this.store.dispatch(fromActions.setCtmMgmtVinsPaginator({ paginator: { page: 1, pageSize: 10 } }));
    }),
  ), { dispatch: false });

  getCtmMgmtVinsList$ = createEffect(() => this.actions$.pipe(
    ofType(fromActions.getCtmMgmtVinsList,
      fromActions.setCtmMgmtVinsPaginator),
    withLatestFrom(
      this.store.select(selectCtmMgmtVins),
    ),
    tap(() => this.store.dispatch(fromActions.setCtmMgmtVinsLoading({ isLoading: true }))),
    switchMap(async ([_, vins]) => {
      const vinsData = await firstValueFrom(this.ctmVehicleProfileService
        .getVehicleProfileVins(
          vins.paginator.page,
          vins.paginator.pageSize,
          undefined,
          vins.filters.qSearch ? vins.filters.qSearch : undefined,
          false));
      return [
        fromActions.setCtmMgmtVinsList({ response: vinsData }),
        fromActions.setCtmMgmtVinsLoading({ isLoading: false }),
      ];
    }),
    catchError(() => of([
      fromActions.setCtmMgmtVinsList({ response: { profiles: [], totalCount: 0 } }),
      fromActions.setCtmMgmtVinsLoading({ isLoading: false }),
    ])),
    switchMap((actions) => [...actions]),
  ));

  setCtmMgmtSignalsFilters$ = createEffect(() => this.actions$.pipe(
    ofType(fromActions.setCtmMgmtSignalsFilters),
    tap(() => {
      this.store.dispatch(fromActions.setCtmMgmtSignalsPaginator({ paginator: { page: 1, pageSize: 10 } }));
    }),
  ), { dispatch: false });

  getCtmMgmtSignalsList$ = createEffect(() => this.actions$.pipe(
    ofType(fromActions.getCtmMgmtSignalsList,
      fromActions.setCtmMgmtSignalsPaginator),
    withLatestFrom(
      this.store.select(selectCtmMgmtSignals),
    ),
    tap(() => this.store.dispatch(fromActions.setCtmMgmtSignalsLoading({ isLoading: true }))),
    switchMap(async ([_, signals]) => {
      const signalsData = await firstValueFrom(this.ctmSignalService
        .getSignals(
          signals.paginator.page,
          signals.paginator.pageSize,
          undefined,
          signals.filters.qSearch ? signals.filters.qSearch : undefined,
          undefined));
      return [
        fromActions.setCtmMgmtSignalsList({ response: signalsData }),
        fromActions.setCtmMgmtSignalsLoading({ isLoading: false }),
      ];
    }),
    catchError(() => of([
      fromActions.setCtmMgmtSignalsList({ response: { items: [], count: 0 } }),
      fromActions.setCtmMgmtSignalsLoading({ isLoading: false }),
    ])),
    switchMap((actions) => [...actions]),
  ));

  setAssignedVinsFilters$ = createEffect(() => this.actions$.pipe(
    ofType(fromActions.setAssignedVinsFilters),
    withLatestFrom(this.store.select(selectAssignedVins)),
    tap(([_, state]) => {
      this.store.dispatch(fromActions.setAssignedVinsPaginator({ paginator: { page: 1, pageSize: 10 } }));
      if (state.profileId) {
        this.store.dispatch(fromActions.getAssignedVinsList({ profileId: state.profileId }));
      }
    }),
  ), { dispatch: false });

  getAssignedVinsList$ = createEffect(() => this.actions$.pipe(
    ofType(fromActions.getAssignedVinsList,
      fromActions.setAssignedVinsPaginator),
    withLatestFrom(
      this.store.select(selectAssignedVins),
    ),
    tap(() => this.store.dispatch(fromActions.setAssignedVinsLoading({ isLoading: true }))),
    switchMap(async ([action, vinsState]) => {
      const profileId = 'profileId' in action ? action.profileId : vinsState.profileId;

      if (!profileId) {
        console.error('ProfileId is required for getAssignedVinsList');
        return [fromActions.setAssignedVinsLoading({ isLoading: false })];
      }

      const vehiclesData = await firstValueFrom(this.ctmVehicleProfileService
        .getVehicleProfileVins(
          vinsState.paginator.page,
          vinsState.paginator.pageSize,
          profileId,
          vinsState.filters.qSearch ? vinsState.filters.qSearch : undefined,
          false));
      return [
        fromActions.setAssignedVinsList({ response: vehiclesData }),
        fromActions.setAssignedVinsLoading({ isLoading: false }),
      ];
    }),
    catchError(() => of([
      fromActions.setAssignedVinsList({ response: { profiles: [], totalCount: 0 } }),
      fromActions.setAssignedVinsLoading({ isLoading: false }),
    ])),
    switchMap((actions) => [...actions]),
  ));

  setAvailableVinsFilters$ = createEffect(() => this.actions$.pipe(
    ofType(fromActions.setAvailableVinsFilters),
    withLatestFrom(this.store.select(selectAvailableVins)),
    tap(([_, state]) => {
      this.store.dispatch(fromActions.setAvailableVinsPaginator({ paginator: { page: 1, pageSize: 10 } }));
      if (state.profileId) {
        this.store.dispatch(fromActions.getAvailableVinsList({ profileId: state.profileId }));
      }
    }),
  ), { dispatch: false });

  getAvailableVinsList$ = createEffect(() => this.actions$.pipe(
    ofType(fromActions.getAvailableVinsList,
      fromActions.setAvailableVinsPaginator),
    withLatestFrom(
      this.store.select(selectAvailableVins),
    ),
    tap(() => this.store.dispatch(fromActions.setAvailableVinsLoading({ isLoading: true }))),
    switchMap(async ([action, vinsState]) => {
      const profileId = 'profileId' in action ? action.profileId : vinsState.profileId;

      if (!profileId) {
        console.error('ProfileId is required for getAvailableVinsList');
        return [fromActions.setAvailableVinsLoading({ isLoading: false })];
      }

      const vehiclesData = await firstValueFrom(this.ctmVehicleProfileService
        .getVehicleProfileVins(
          vinsState.paginator.page,
          vinsState.paginator.pageSize,
          profileId,
          vinsState.filters.qSearch ? vinsState.filters.qSearch : undefined,
          true));
      return [
        fromActions.setAvailableVinsList({ response: vehiclesData }),
        fromActions.setAvailableVinsLoading({ isLoading: false }),
      ];
    }),
    catchError(() => of([
      fromActions.setAvailableVinsList({ response: { profiles: [], totalCount: 0 } }),
      fromActions.setAvailableVinsLoading({ isLoading: false }),
    ])),
    switchMap((actions) => [...actions]),
  ));

  // Assigned Signals Effects
  setAssignedSignalsFilters$ = createEffect(() => this.actions$.pipe(
    ofType(fromActions.setAssignedSignalsFilters),
    withLatestFrom(this.store.select(selectAssignedSignals)),
    tap(([_, state]) => {
      this.store.dispatch(fromActions.setAssignedSignalsPaginator({ paginator: { page: 1, pageSize: this.signalsPageSize } }));
      if (state.profileId && state.appType !== undefined && state.canLineNumber !== undefined) {
        this.store.dispatch(fromActions.getAssignedSignalsList({
          profileId: state.profileId,
          appType: state.appType,
          canLineNumber: state.canLineNumber
        }));
      }
    }),
  ), { dispatch: false });

  getAssignedSignalsList$ = createEffect(() => this.actions$.pipe(
    ofType(fromActions.getAssignedSignalsList,
      fromActions.setAssignedSignalsPaginator),
    withLatestFrom(
      this.store.select(selectAssignedSignals),
      this.store.select(selectProfile),
    ),
    tap(() => this.store.dispatch(fromActions.setAssignedSignalsLoading({ isLoading: true }))),
    switchMap(async ([action, signalsState, profile]) => {
      const profileId = 'profileId' in action ? action.profileId : signalsState.profileId;
      const appType = 'appType' in action ? action.appType : signalsState.appType;
      const canLineNumber = 'canLineNumber' in action ? action.canLineNumber : signalsState.canLineNumber;

      if (!profileId || appType === undefined || canLineNumber === undefined) {
        console.error('ProfileId, appType and canLineNumber are required for getAssignedSignalsList');
        return [fromActions.setAssignedSignalsLoading({ isLoading: false })];
      }

      // Get assigned signals from profile Redux object
      let assignedSignals: any[] = [];
      if (profile && profile.apps) {
        const app = profile.apps.find((a: any) => a.typeData === appType);
        if (app && app.canLines) {
          const canLine = app.canLines.find((cl: any) => cl.canLineNumber === canLineNumber);
          if (canLine && canLine.signals) {
            assignedSignals = canLine.signals;
          }
        }
      }

      // Apply pagination and filtering
      let filteredSignals = assignedSignals;
      if (signalsState.filters.qSearch) {
        const search = signalsState.filters.qSearch.toLowerCase();
        filteredSignals = assignedSignals.filter((signal: any) =>
          signal.name?.toLowerCase().includes(search) ||
          signal.category?.toLowerCase().includes(search)
        );
      }

      const startIndex = (signalsState.paginator.page - 1) * signalsState.paginator.pageSize;
      const endIndex = startIndex + signalsState.paginator.pageSize;
      const paginatedSignals = filteredSignals.slice(startIndex, endIndex);

      const signalsData = {
        items: paginatedSignals,
        count: filteredSignals.length
      };

      return [
        fromActions.setAssignedSignalsList({ response: signalsData }),
        fromActions.setAssignedSignalsLoading({ isLoading: false }),
      ];
    }),
    switchMap((actions) => [...actions]),
  ));

  // Available Signals Effects
  setAvailableSignalsFilters$ = createEffect(() => this.actions$.pipe(
    ofType(fromActions.setAvailableSignalsFilters),
    withLatestFrom(this.store.select(selectAvailableSignals)),
    tap(([_, state]) => {
      this.store.dispatch(fromActions.setAvailableSignalsPaginator({ paginator: { page: 1, pageSize: this.signalsPageSize } }));
      if (state.profileId && state.appType !== undefined && state.canLineNumber !== undefined) {
        this.store.dispatch(fromActions.getAvailableSignalsList({
          profileId: state.profileId,
          appType: state.appType,
          canLineNumber: state.canLineNumber
        }));
      }
    }),
  ), { dispatch: false });

  getAvailableSignalsList$ = createEffect(() => this.actions$.pipe(
    ofType(fromActions.getAvailableSignalsList,
      fromActions.setAvailableSignalsPaginator),
    withLatestFrom(
      this.store.select(selectAvailableSignals),
      this.store.select(selectProfile),
    ),
    tap(() => this.store.dispatch(fromActions.setAvailableSignalsLoading({ isLoading: true }))),
    switchMap(async ([action, signalsState, profile]) => {
      const profileId = 'profileId' in action ? action.profileId : signalsState.profileId;
      const appType = 'appType' in action ? action.appType : signalsState.appType;
      const canLineNumber = 'canLineNumber' in action ? action.canLineNumber : signalsState.canLineNumber;

      if (!profileId || appType === undefined || canLineNumber === undefined) {
        console.error('ProfileId, appType and canLineNumber are required for getAvailableSignalsList');
        return [fromActions.setAvailableSignalsLoading({ isLoading: false })];
      }

      // Get signals filtered by canLineNumber
      const signalsData = await firstValueFrom(this.ctmSignalService
        .getSignals(
          signalsState.paginator.page,
          signalsState.paginator.pageSize,
          undefined,
          signalsState.filters.qSearch ? signalsState.filters.qSearch : undefined,
          canLineNumber));

      // Get assigned signal IDs from profile Redux object for this app+canLine
      const assignedSignalIds = new Set<string>();
      if (profile && profile.apps) {
        const app = profile.apps.find((a: any) => a.typeData === appType);
        if (app && app.canLines) {
          const canLine = app.canLines.find((cl: any) => cl.canLineNumber === canLineNumber);
          if (canLine && canLine.signals) {
            canLine.signals.forEach((signal: any) => {
              if (signal.paramId) {
                assignedSignalIds.add(signal.paramId);
              }
            });
          }
        }
      }

      // Filter out already assigned signals
      const availableSignals = (signalsData.items || []).filter((signal: any) =>
        !assignedSignalIds.has(signal.paramId)
      );

      const filteredSignalsData = {
        items: availableSignals,
        count: Math.max((signalsData.count || 0) - assignedSignalIds.size, availableSignals.length)
      };

      return [
        fromActions.setAvailableSignalsList({ response: filteredSignalsData }),
        fromActions.setAvailableSignalsLoading({ isLoading: false }),
      ];
    }),
    catchError(() => of([
      fromActions.setAvailableSignalsList({ response: { items: [], count: 0 } }),
      fromActions.setAvailableSignalsLoading({ isLoading: false }),
    ])),
    switchMap((actions) => [...actions]),
  ));

  constructor(
    private actions$: Actions,
    private store: Store,
    private ctmProfileService: CtmProfileService,
    private ctmFallbackService: CtmFallbackService,
    private ctmSignalService: CtmSignalService,
    private ctmVehicleProfileService: CtmConfigurationVehicleProfileService,
  ) {
  }
}
